import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface User {
    id: string;
    email: string | null;
    phone?: string | null;
    phone_verified?: boolean;
}

interface AuthState {
    loading: boolean;
    error: string | null;
    user: { id: string; email: string; phone?: string; phone_verified?: boolean } | null;
    initialized: boolean;
    signUpComplete: boolean;
    forgotPasswordComplete?: boolean;
    lastPasswordResetRequest?: number;
    passwordResetCooldown: boolean;
}

interface SignInPayload {
    user: {
        id: string;
        email: string;
        phone?: string;
        phone_verified?: boolean;
    };
    token: string;
    refreshToken?: string;
}

export interface AuthCredentials {
    email?: string;
    phone?: string;
    password: string;
}

const initialState: AuthState = {
    loading: false,
    error: null,
    user: null,
    initialized: false,
    signUpComplete: false,
    passwordResetCooldown: false,
};

interface ResetPasswordPayload {
    newPassword: string;
}

export interface ForgotPasswordPayload {
    email: string;
}
interface CodeExchangePayload {
    code: string;
}

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        //--- INITIALIZE
        initializeRequest: (state) => {
            // Don't reinitialize if already initialized with a user
            // This prevents navigation resets during Stripe flow and other scenarios
            if (state.initialized && state.user) {
                console.log('[AUTH] Skipping initializeRequest - already initialized with user');
                return;
            }
            state.loading = true;
            state.error = null;
        },
        initializeSuccess: (state, action: PayloadAction<User>) => {
            state.loading = false;
            state.error = null;
            //@ts-ignore
            state.user = action.payload;
            state.initialized = true;
        },
        initializeFailure: (state, action: PayloadAction<string | undefined>) => {
            state.loading = false;
            state.error = action.payload || null;
            state.user = null;
            state.initialized = true;
        },

        //--- SIGN IN
        signInRequest: (state, _action: PayloadAction<AuthCredentials>) => {
            state.loading = true;
            state.error = null;
        },
        signInSuccess: (state, action: PayloadAction<SignInPayload>) => {
            state.loading = false;
            state.error = null;
            state.user = action.payload.user;

            (state as any).accessToken = action.payload.token;
            (state as any).refreshToken = action.payload.refreshToken;
        },
        signInFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        //--- SIGN UP
        signUpRequest: (state, _action: PayloadAction<AuthCredentials>) => {
            console.log('Sign up request', state);
            state.loading = true;
            state.error = null;
            state.signUpComplete = false;
        },
        signUpSuccess: (state) => {
            console.log('Sign up success', state);
            state.loading = false;
            state.error = null;
            state.signUpComplete = true;
        },
        signUpFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
            state.signUpComplete = false;
        },

        //--- SIGN OUT
        signOutRequest: (state) => {
        },
        signOutSuccess: (state) => {
            state.user = null;
            state.error = null;
            state.loading = false;
            state.signUpComplete = false;
        },
        signOutFailure: (state, action: PayloadAction<string>) => {
            state.error = action.payload;
        },

        //--- FORGOT PASSWORD
        forgotPasswordRequest: (state, _action: PayloadAction<ForgotPasswordPayload>) => {
            state.loading = true;
            state.error = null;
            state.forgotPasswordComplete = false;
        },
        forgotPasswordSuccess: (state) => {
            state.loading = false;
            state.error = null;
            state.forgotPasswordComplete = true;
            state.lastPasswordResetRequest = Date.now();
            state.passwordResetCooldown = true;
        },
        forgotPasswordFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
            state.forgotPasswordComplete = false;
        },
        // For the final step of resetting password
        resetPasswordRequest: (state, _action: PayloadAction<ResetPasswordPayload>) => {
            state.loading = true;
            state.error = null;
        },
        resetPasswordSuccess: (state) => {
            state.loading = false;
            state.error = null;
        },
        resetPasswordFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },

        codeExchangeRequest: (state, _action: PayloadAction<CodeExchangePayload>) => {
            state.loading = true;
            state.error = null;
        },
        codeExchangeFailure: (state, action: PayloadAction<string>) => {
            state.loading = false;
            state.error = action.payload;
        },
        
        // Reset forgot password state
        resetForgotPasswordState: (state) => {
            state.forgotPasswordComplete = false;
            state.error = null;
        },
        // Clear password reset cooldown
        clearPasswordResetCooldown: (state) => {
            state.passwordResetCooldown = false;
        },
    },
});

export const {
    initializeRequest,
    initializeSuccess,
    initializeFailure,
    signInRequest,
    signInSuccess,
    signInFailure,
    signUpRequest,
    signUpSuccess,
    signUpFailure,
    signOutRequest,
    signOutSuccess,
    signOutFailure,
    forgotPasswordRequest,
    forgotPasswordSuccess,
    forgotPasswordFailure,
    resetPasswordRequest,
    resetPasswordSuccess,
    resetPasswordFailure,
    codeExchangeRequest,
    codeExchangeFailure,
    resetForgotPasswordState,
    clearPasswordResetCooldown,
} = authSlice.actions;

export const authReducer = authSlice.reducer;